/**
 * Location Service V2 - City-focused location search
 * Provides city-level location search functionality using Firebase Functions as proxy to Google Places API
 */

import { app } from './firebase/firebase-config';

// City-level place types to include
const CITY_TYPES = ['locality', 'administrative_area_level_3', 'sublocality'];

// Address-level types to exclude
const EXCLUDED_TYPES = ['street_address', 'route', 'premise', 'street_number', 'establishment'];

/**
 * LocationResult data model
 */
export class LocationResult {
    constructor(data) {
        this.id = data.place_id;
        this.displayName = data.structured_formatting?.main_text || data.description || '';
        this.city = this.extractCity(data);
        this.state = this.extractState(data);
        this.country = this.extractCountry(data);
        this.formattedAddress = data.description || '';
        this.coordinates = null; // Will be populated when details are fetched
    }

    extractCity(data) {
        if (data.structured_formatting?.main_text) {
            return data.structured_formatting.main_text;
        }
        return data.description?.split(',')[0] || '';
    }

    extractState(data) {
        if (data.structured_formatting?.secondary_text) {
            const parts = data.structured_formatting.secondary_text.split(',');
            return parts[0]?.trim() || '';
        }
        const parts = data.description?.split(',') || [];
        return parts[1]?.trim() || '';
    }

    extractCountry(data) {
        if (data.structured_formatting?.secondary_text) {
            const parts = data.structured_formatting.secondary_text.split(',');
            return parts[parts.length - 1]?.trim() || '';
        }
        const parts = data.description?.split(',') || [];
        return parts[parts.length - 1]?.trim() || '';
    }
}

/**
 * LocationDetails data model for storage
 */
export class LocationDetails {
    constructor(data, coordinates = null) {
        this.id = data.place_id;
        this.displayName = data.name || data.formatted_address?.split(',')[0] || '';
        this.city = this.extractCityFromDetails(data);
        this.state = this.extractStateFromDetails(data);
        this.country = this.extractCountryFromDetails(data);
        this.coordinates = coordinates || this.extractCoordinates(data);
        this.formattedAddress = data.formatted_address || '';
        this.timestamp = new Date();
    }

    extractCityFromDetails(data) {
        const cityComponent = data.address_components?.find(component =>
            component.types.some(type => CITY_TYPES.includes(type))
        );
        return cityComponent?.long_name || data.name || '';
    }

    extractStateFromDetails(data) {
        const stateComponent = data.address_components?.find(component =>
            component.types.includes('administrative_area_level_1')
        );
        return stateComponent?.long_name || '';
    }

    extractCountryFromDetails(data) {
        const countryComponent = data.address_components?.find(component =>
            component.types.includes('country')
        );
        return countryComponent?.long_name || '';
    }

    extractCoordinates(data) {
        if (data.geometry?.location) {
            return {
                lat: data.geometry.location.lat,
                lng: data.geometry.location.lng
            };
        }
        return null;
    }
}

/**
 * Cache for storing recent search results
 */
class SearchCache {
    constructor(ttl = 5 * 60 * 1000) { // 5 minutes TTL
        this.cache = new Map();
        this.ttl = ttl;
    }

    get(key) {
        const item = this.cache.get(key);
        if (!item) return null;

        if (Date.now() - item.timestamp > this.ttl) {
            this.cache.delete(key);
            return null;
        }

        return item.data;
    }

    set(key, data) {
        this.cache.set(key, {
            data,
            timestamp: Date.now()
        });
    }

    clear() {
        this.cache.clear();
    }
}

const searchCache = new SearchCache();

/**
 * Filters results to only include city-level locations
 */
function filterCityResults(predictions) {
    return predictions.filter(prediction => {
        // Check if any of the prediction types are city-level
        const hasCityType = prediction.types?.some(type => CITY_TYPES.includes(type));

        // Check if any of the prediction types are address-level (exclude these)
        const hasAddressType = prediction.types?.some(type => EXCLUDED_TYPES.includes(type));

        // Include if it has city types and doesn't have address types
        return hasCityType && !hasAddressType;
    });
}

/**
 * Validates coordinates are within valid ranges
 */
function validateCoordinates(coordinates) {
    if (!coordinates || typeof coordinates.lat !== 'number' || typeof coordinates.lng !== 'number') {
        return false;
    }

    return coordinates.lat >= -90 && coordinates.lat <= 90 &&
        coordinates.lng >= -180 && coordinates.lng <= 180;
}

/**
 * Location Service V2 API
 */
export const locationServiceV2 = {
    /**
     * Search for cities based on query
     * @param {string} query - Search query
     * @returns {Promise<Array<LocationResult>>} Array of city results
     */
    async searchCities(query) {
        if (!query || query.length < 2) {
            return [];
        }

        // Check cache first
        const cacheKey = `search_${query.toLowerCase()}`;
        const cachedResults = searchCache.get(cacheKey);
        if (cachedResults) {
            return cachedResults;
        }

        try {
            // Use Firebase Function V2 as proxy to Google Places API (HTTP endpoint)
            const functionUrl = `https://us-central1-${app.options.projectId}.cloudfunctions.net/placesAutocompleteV2`;

            console.log('🔍 Calling placesAutocompleteV2 with:', { query, projectId: app.options.projectId, functionUrl });

            const response = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    input: query,
                    sessionToken: this.generateSessionToken()
                })
            });

            const result = await response.json();
            console.log('📡 Function response:', result);

            if (!result.success) {
                throw new Error('Places API request failed');
            }

            const suggestions = result.suggestions || [];

            // Convert Firebase Function response to LocationResult objects
            const results = suggestions
                .slice(0, 8) // Limit to 8 results for performance
                .map(suggestion => new LocationResult({
                    place_id: suggestion.placeId,
                    description: suggestion.description,
                    structured_formatting: {
                        main_text: suggestion.mainText || suggestion.displayName,
                        secondary_text: suggestion.secondaryText
                    }
                }));

            // Cache the results
            searchCache.set(cacheKey, results);

            return results;
        } catch (error) {
            console.error('Error searching cities:', error);
            throw new Error('Unable to search locations. Please try again.');
        }
    },

    /**
     * Get detailed information for a specific city
     * @param {string} placeId - Google Places ID
     * @returns {Promise<LocationDetails>} Detailed location information
     */
    async getCityDetails(placeId) {
        if (!placeId) {
            throw new Error('Place ID is required');
        }

        // Check cache first
        const cacheKey = `details_${placeId}`;
        const cachedDetails = searchCache.get(cacheKey);
        if (cachedDetails) {
            return cachedDetails;
        }

        try {
            // Use Firebase Function V2 as proxy to Google Places API (HTTP endpoint)
            const functionUrl = `https://us-central1-${app.options.projectId}.cloudfunctions.net/placeDetailsV2`;

            const response = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    placeId: placeId,
                    sessionToken: this.generateSessionToken()
                })
            });

            const result = await response.json();

            if (!result.success) {
                throw new Error('Place details request failed');
            }

            const locationData = result.location;

            // Convert Firebase Function response to LocationDetails format
            const details = new LocationDetails({
                place_id: locationData.placeId,
                name: locationData.displayName,
                formatted_address: locationData.formattedAddress,
                address_components: this.convertAddressComponents(locationData.components),
                geometry: {
                    location: {
                        lat: locationData.coordinates?.lat,
                        lng: locationData.coordinates?.lng
                    }
                },
                types: locationData.types || []
            });

            // Validate coordinates
            if (details.coordinates && !validateCoordinates(details.coordinates)) {
                console.warn('Invalid coordinates received:', details.coordinates);
                details.coordinates = null;
            }

            // Cache the details
            searchCache.set(cacheKey, details);

            return details;
        } catch (error) {
            console.error('Error fetching city details:', error);
            throw new Error('Unable to fetch location details. Please try again.');
        }
    },

    /**
     * Clear the search cache
     */
    clearCache() {
        searchCache.clear();
    },

    /**
     * Generate a session token for Google Places API
     */
    generateSessionToken() {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    },

    /**
     * Convert Firebase Function address components format to Google Places format
     */
    convertAddressComponents(components) {
        if (!components) return [];

        const addressComponents = [];

        if (components.city) {
            addressComponents.push({
                long_name: components.city,
                short_name: components.city,
                types: ['locality', 'political']
            });
        }

        if (components.state) {
            addressComponents.push({
                long_name: components.state,
                short_name: components.state,
                types: ['administrative_area_level_1', 'political']
            });
        }

        if (components.country) {
            addressComponents.push({
                long_name: components.country,
                short_name: components.countryCode || components.country,
                types: ['country', 'political']
            });
        }

        if (components.postalCode) {
            addressComponents.push({
                long_name: components.postalCode,
                short_name: components.postalCode,
                types: ['postal_code']
            });
        }

        return addressComponents;
    }
};

export default locationServiceV2;